/**
 * The last line of defence (§38).
 *
 * §38: "Never show stack traces, SQL/provider errors, internal IDs or
 * secrets." A React render crash would otherwise put a component stack on a
 * child's screen. This catches it, shows a friendly recovery, and sends the
 * detail to monitoring instead.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: (reset: () => void) => ReactNode;
}

interface State {
  crashed: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { crashed: false };

  static getDerivedStateFromError(): State {
    return { crashed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Console in development; a monitoring hook in production. Never rendered.
    console.error('[preplift] render crash', error, info.componentStack);
    window.dispatchEvent(
      new CustomEvent('preplift:crash', { detail: { message: error.message } }),
    );
  }

  private readonly reset = (): void => {
    this.setState({ crashed: false });
  };

  override render(): ReactNode {
    if (!this.state.crashed) return this.props.children;
    if (this.props.fallback) return this.props.fallback(this.reset);

    return (
      <main className="app-content flex min-h-dvh flex-col items-center justify-center gap-4 text-center">
        <span aria-hidden="true" className="text-5xl">
          😕
        </span>
        <p className="text-[length:var(--text-lg)]">Something went wrong. Please try again.</p>
        <button
          type="button"
          onClick={() => window.location.assign('/')}
          className="min-h-[var(--tap)] rounded-[var(--radius-lg)] bg-[var(--color-primary)] px-6 font-[var(--weight-semibold)] text-white"
        >
          Go home
        </button>
      </main>
    );
  }
}
